/**
 * The recording bar menu's vocabulary and windowing (#13804, Wave 3 of #13791).
 *
 * The menu behind the bar's chevron says four kinds of thing: which microphone
 * is selected, where an interrupted recording stopped, what is on the viewer's
 * calendar today, and that a capture can start with no meeting at all. Every
 * sentence it says, and the day window it says the third one about, is decided
 * here — once, as a pure function — so the server that formats a label and the
 * component that renders it cannot disagree about what a day is or how a
 * count is spelled.
 *
 * ## Client-safe, deliberately
 *
 * No `server-only`, no Prisma, no DOM. `src/app/actions/recording-bar-menu.ts`
 * imports the formatters to pre-render labels in the VIEWER's zone (ADR-0101),
 * and `src/components/recording-bar/recording-bar-menu.tsx` imports the rest.
 * Same split, and same reason, as `./recording-bar`.
 *
 * ## Why the day window takes a timezone rather than reading a clock
 *
 * "On your calendar today" is a claim about the viewer's day. A server running
 * in UTC deciding it silently shows tomorrow's meetings to anyone west of
 * Greenwich after their evening rolls the UTC date over — the artboard's
 * *Admin team weekly · 8:30 · in 6 min* is a morning row, and a person opening
 * the menu at 21:30 in Denver would be looking at the wrong day's list under a
 * heading that says today.
 */

/**
 * How many of today's meetings the menu will list.
 *
 * A bound rather than a page: the menu is app chrome anchored to the bottom of
 * the viewport and opens UPWARD, so an unbounded list on a heavily-booked day
 * would run off the top of the screen with the items nearest the cursor —
 * which is the ones this layout exists to put in reach — pushed out of view.
 * Somebody with more than this many meetings today reaches the rest through
 * the menu's "See all upcoming" link to `/events` (#14402), which is a list
 * designed to be long; the server reads one row past this bound to know.
 */
export const RECORDING_MENU_MEETING_LIMIT = 8

/**
 * How quiet a capture's storage must go before it counts as INTERRUPTED.
 *
 * `Recording` has no "still alive" column: a capture that is running right now
 * and one whose tab closed sixty seconds ago are the same row in the same
 * status, because the only thing that ever settles a capture is the client
 * coming back to settle it. So "is this one still going?" can only be answered
 * by the bytes, and the answer is how long ago the newest chunk landed.
 *
 * Derived from {@link RECORDING_MAX_CHUNK_MS} (20s) rather than picked: a live
 * capture PUTs a chunk at least that often, so three chunk-intervals of
 * silence is a capture that has stopped uploading. The margin is deliberately
 * generous in the direction of NOT offering — briefly mislabelling a live
 * capture as unavailable to resume costs somebody one menu row, while the
 * opposite error hands them a second `MediaRecorder` writing over the first
 * one's chunk indices.
 */
export const RECORDING_MENU_LIVE_CAPTURE_GRACE_MS = 60_000

/**
 * How many unsettled captures a resumable-capture scan will look at.
 *
 * A bound, because each candidate costs one bucket listing. It is not a page:
 * the scan wants the newest capture that actually holds audio, and the rows it
 * skips are empty or still-live ones — both of which are produced by recent
 * activity, so the answer is always near the top. Four covers a person who
 * denied permission three times in a row and still has a real interrupted
 * capture underneath, which is further than this has ever needed to reach.
 *
 * HERE RATHER THAN BESIDE ITS FIRST READER (#14051): two readers scan for the
 * same thing now — the bar menu's `readInterruptedCapture` and the meeting
 * page's posture read — and the claim that they "cannot disagree about what is
 * resumable" is only true while they share the bound as well as the predicate.
 * `src/app/actions/recording-bar-menu.ts` is a `'use server'` module and
 * cannot export a constant, so the shared home is this one.
 */
export const RECORDING_MENU_INTERRUPTED_SCAN_LIMIT = 4

/**
 * How much of the menu a read asks for (#15666 review). `'all'` is the
 * menu's open — today's meetings and the interrupted capture. `'interrupted'`
 * is the host's read on mount and on every route change, which only needs
 * the capture that brings an idle bar back: it skips the calendar read.
 * The query parameter `/api/shell/recording-menu` takes it as.
 */
export const RECORDING_MENU_SCOPES = ['all', 'interrupted'] as const
export type RecordingMenuScope = (typeof RECORDING_MENU_SCOPES)[number]
export const RECORDING_MENU_SCOPE_PARAM = 'scope'

/** Wire input: anything but a known scope is the whole menu. */
export function toRecordingMenuScope(value: unknown): RecordingMenuScope {
  return RECORDING_MENU_SCOPES.find(known => known === value) ?? 'all'
}

/**
 * The label for a microphone the browser has not named.
 *
 * Shown as the selected device before permission is granted, and as the
 * standing "let the browser choose" option afterwards. It is an honest
 * statement rather than a guess at a device name: `enumerateDevices()` returns
 * unlabelled entries until consent, and inventing "MacBook Pro Microphone"
 * from nothing would be a sentence the product cannot support.
 */
export const DEFAULT_AUDIO_INPUT_LABEL = 'Default microphone'

/** One normalized audio input, as the menu renders it. */
export type AudioInputDevice = {
  deviceId: string
  label: string
}

/** The shape of `MediaDeviceInfo` this module needs, and nothing more. */
export type EnumeratedDevice = {
  deviceId: string
  kind: string
  label: string
}

/**
 * The audio inputs worth offering, from a raw `enumerateDevices()` answer.
 *
 * Four filters, each covering a real browser behaviour:
 *
 *  - **kind** — the answer includes cameras and speakers, and a microphone
 *    picker offering "MacBook Pro Speakers" is simply wrong;
 *  - **blank labels** — before the permission prompt every label is `''`. A
 *    list of unnamed rows is not a choice a person can make, so an
 *    unpermissioned enumeration normalizes to NOTHING and the component shows
 *    only {@link DEFAULT_AUDIO_INPUT_LABEL}. Re-enumerating after consent is
 *    what replaces it with real names;
 *  - **duplicate ids** — Chrome reports the same physical device more than
 *    once across groups, and two identical rows read as a rendering bug;
 *  - **pseudo-devices** — Chrome ALSO reports two synthetic entries whose ids
 *    are the literals `default` and `communications`, each an alias for a
 *    physical device that is in the list again under its own id. Their ids
 *    differ, so the duplicate-id filter above does not catch them, and their
 *    labels are the aliased device's name with a `Default - ` /
 *    `Communications - ` prefix — so the list reads as the same microphone
 *    three times. They are dropped rather than de-prefixed: the standing
 *    "let the browser choose" option is already offered as
 *    {@link DEFAULT_AUDIO_INPUT_LABEL}, which is what `default` means, and
 *    offering it twice under two names is the duplicate one level up.
 */
export function normalizeAudioInputDevices(
  devices: readonly EnumeratedDevice[],
): AudioInputDevice[] {
  const seen = new Set<string>()
  const result: AudioInputDevice[] = []
  for (const device of devices) {
    if (device.kind !== 'audioinput') continue
    if (!device.deviceId) continue
    if (PSEUDO_AUDIO_INPUT_IDS.has(device.deviceId)) continue
    if (!device.label.trim()) continue
    if (seen.has(device.deviceId)) continue
    seen.add(device.deviceId)
    result.push({ deviceId: device.deviceId, label: device.label })
  }
  return result
}

/**
 * Chrome's synthetic audio-input ids — aliases, not devices.
 *
 * Both name a physical microphone that `enumerateDevices()` also returns under
 * its own id, so keeping them shows one microphone under several names. The
 * literals are Chrome's, not ours; other engines simply never emit them.
 */
const PSEUDO_AUDIO_INPUT_IDS: ReadonlySet<string> = new Set([
  'default',
  'communications',
])

/**
 * The attendee count, or `null` when there is nothing true to say.
 *
 * `null` rather than "0 attendees": the read that produces these rows is
 * narrowed to meetings the viewer ATTENDS, so a zero is a data artifact and
 * not a fact about the meeting. Callers join the non-null parts, so a `null`
 * removes its separator too rather than leaving a dangling middle dot.
 */
export function formatAttendeeCount(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null
  return count === 1 ? '1 attendee' : `${count} attendees`
}

/**
 * *Stopped at 6:04 PM when the tab closed* — the artboard's whole affordance.
 *
 * The sentence is doing one job: telling somebody that the audio is not lost.
 * A recording whose tab closed mid-meeting stays in progress (see
 * `findResumableRecording`) until the transcription cron's abandoned-capture
 * sweep settles it `RECORDING_ABANDONED_AFTER_MS` later (#14429); from then
 * on it is a finished note, not a row this menu offers.
 *
 * It is not the only surface that says so. `/notetaker/record` already reads
 * the same `findResumableRecording` and offers the same capture
 * (`src/app/notetaker/record/page.tsx`); what this row adds is that the offer
 * reaches app chrome, so it no longer requires navigating to the record page
 * to discover. Both being live is exactly why `readInterruptedCapture` has to
 * exclude a capture that is RUNNING rather than stopped — see its own
 * docblock.
 */
export function formatInterruptedStopDetail(stoppedAtLabel: string): string {
  return `Stopped at ${stoppedAtLabel} when the tab closed`
}

/** *Finish Tuesday's recording* — the group heading above that row. */
export function finishRecordingGroupLabel(weekdayLabel: string): string {
  return `Finish ${weekdayLabel}’s recording`
}

/**
 * The weekday a moment fell on, in the VIEWER's display zone.
 *
 * `timeZone` is REQUIRED for the reason `formatRecordingBarStartTime` states:
 * a default turns "the viewer's zone" into "whatever zone the server happens
 * to run in" at every call site that forgets one — and a recording made on
 * Tuesday evening in Denver is already Wednesday in UTC, so the default would
 * offer to finish a recording on a day the user never recorded.
 */
export function formatRecordingBarWeekday(
  date: Date,
  timeZone: string,
): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(
    date,
  )
}

type ZonedParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date)
  const read = (type: string) =>
    Number(parts.find(part => part.type === type)?.value ?? '0')
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    // Some ICU builds spell midnight as hour 24 under `hour12: false`.
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
  }
}

/**
 * How far `timeZone` is from UTC at `date`, in ms.
 *
 * Derived by formatting the instant into the zone and reading the wall clock
 * back — the standard trick, and the only one available without a timezone
 * library. Milliseconds are subtracted out because `Date.UTC` has no
 * millisecond field to carry them.
 */
function zonedOffsetMs(date: Date, timeZone: string): number {
  const parts = zonedParts(date, timeZone)
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  )
  return asUtc - (date.getTime() - date.getUTCMilliseconds())
}

/**
 * The half-open window `[midnight, next midnight)` of the viewer's day.
 *
 * DST-SAFE BY CONSTRUCTION, and that is why each boundary resolves its own
 * offset instead of one offset being applied to both. On a spring-forward day
 * the local day is 23 hours long: `start + 24h` would land at 01:00 the next
 * morning and pull tomorrow's first meeting into today's list, under a heading
 * that says today. Each end is converted with the offset in force AT that end,
 * so the window is exactly the local day however long that happens to be.
 */
export function todayWindowInTimeZone(
  nowMs: number,
  timeZone: string,
): { start: Date; end: Date } {
  const now = new Date(nowMs)
  const parts = zonedParts(now, timeZone)
  const approximateOffset = zonedOffsetMs(now, timeZone)

  const resolve = (utcMidnight: number): Date => {
    // Two passes: the first lands within an hour of the boundary, which is
    // close enough to read the offset that actually applies there.
    const approximate = new Date(utcMidnight - approximateOffset)
    return new Date(utcMidnight - zonedOffsetMs(approximate, timeZone))
  }

  return {
    start: resolve(Date.UTC(parts.year, parts.month - 1, parts.day)),
    end: resolve(Date.UTC(parts.year, parts.month - 1, parts.day + 1)),
  }
}
